/**
 * Shared API Utilities
 * 
 * Common functionality used across all API services to eliminate code duplication.
 * Provides consistent error handling, authentication, retry logic, and response transformation.
 */

/**
 * Get base URL from environment with fallback
 * @returns {string} Base API URL
 */
export const getBaseUrl = () => {
  if (process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL.replace(/\/$/, '');
  }
  return 'https://api.staging.onsolosocial.com';
};

/**
 * Get authentication token from localStorage
 * @returns {string|null} Auth token
 */
export const getAuthToken = () => {
  return localStorage.getItem('token');
};

/**
 * Build query parameters from an object
 * @param {Object} params - Parameters object
 * @returns {URLSearchParams} URL search parameters
 */
export const buildQueryParams = (params = {}) => {
  const searchParams = new URLSearchParams();
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      if (Array.isArray(value)) {
        searchParams.append(key, value.join(','));
      } else {
        searchParams.append(key, value.toString());
      }
    }
  });
  
  return searchParams;
};

/**
 * Enhanced API request with retry logic and comprehensive error handling
 * @param {string} baseUrl - Base URL for the API
 * @param {string} endpoint - API endpoint (relative to base)
 * @param {Object} options - Fetch options
 * @param {number} retryAttempts - Number of retry attempts
 * @returns {Promise} API response
 */
export const apiRequest = async (baseUrl, endpoint, options = {}, retryAttempts = 0) => {
  const token = getAuthToken();
  
  console.log('API Request Debug:', {
    hasToken: !!token,
    tokenLength: token ? token.length : 0,
    endpoint,
    method: options.method || 'GET'
  });
  
  if (!token) {
    console.error('No authentication token found in localStorage');
    throw new Error('Authentication required. Please log in.');
  }

  const url = endpoint.startsWith('http') ? endpoint : `${baseUrl}${endpoint}`;
  
  const config = {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Token ${token}`,
      ...options.headers,
    },
    ...options,
  };

  for (let attempt = 0; attempt <= retryAttempts; attempt++) {
    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch {
          errorData = { message: `HTTP ${response.status}: ${response.statusText}` };
        }
        
        const errorMessage = errorData.error || 
                            errorData.message || 
                            errorData.detail ||
                            `Request failed with status ${response.status}`;
        
        // Don't retry on client errors (4xx)
        if (response.status >= 400 && response.status < 500) {
          throw new Error(errorMessage);
        }
        
        // Retry on server errors (5xx) or network errors
        if (attempt === retryAttempts) {
          throw new Error(errorMessage);
        }
        
        // Exponential backoff
        await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
        continue;
      }

      // Handle empty responses
      if (response.status === 204) {
        return { success: true };
      }

      const data = await response.json();
      return data;
      
    } catch (error) {
      if (attempt === retryAttempts) {
        console.error(`API request failed for ${url} after ${retryAttempts + 1} attempts:`, error);
        throw error;
      }
      
      // Wait before retry
      await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
    }
  }
};

/**
 * Create an API service class with common functionality
 * @param {string} serviceName - Name of the service for logging
 * @param {string} baseEndpoint - Base endpoint path (e.g., '/api/campaigns')
 * @returns {Object} API service instance
 */
export const createApiService = (serviceName, baseEndpoint) => {
  const serviceBaseUrl = `${getBaseUrl()}${baseEndpoint}`;
  
  return {
    /**
     * Make an API request for this service
     * @param {string} endpoint - Endpoint relative to service base
     * @param {Object} options - Request options
     * @param {number} retryAttempts - Retry attempts
     * @returns {Promise} API response
     */
    request: async (endpoint, options = {}, retryAttempts = 0) => {
      return apiRequest(serviceBaseUrl, endpoint, options, retryAttempts);
    },

    /**
     * Make a GET request
     * @param {string} endpoint - Endpoint
     * @param {Object} params - Query parameters
     * @param {Object} options - Additional options
     * @returns {Promise} API response
     */
    get: async (endpoint, params = {}, options = {}) => {
      const queryParams = buildQueryParams(params);
      const url = queryParams.toString() 
        ? `${endpoint}${endpoint.includes('?') ? '&' : '?'}${queryParams}` 
        : endpoint;
      
      return apiRequest(serviceBaseUrl, url, { method: 'GET', ...options });
    },

    /**
     * Make a POST request
     * @param {string} endpoint - Endpoint
     * @param {Object} data - Request body data
     * @param {Object} options - Additional options
     * @returns {Promise} API response
     */
    post: async (endpoint, data = {}, options = {}) => {
      return apiRequest(serviceBaseUrl, endpoint, {
        method: 'POST',
        body: JSON.stringify(data),
        ...options,
      });
    },

    /**
     * Make a PUT request
     * @param {string} endpoint - Endpoint
     * @param {Object} data - Request body data
     * @param {Object} options - Additional options
     * @returns {Promise} API response
     */
    put: async (endpoint, data = {}, options = {}) => {
      return apiRequest(serviceBaseUrl, endpoint, {
        method: 'PUT',
        body: JSON.stringify(data),
        ...options,
      });
    },

    /**
     * Make a PATCH request
     * @param {string} endpoint - Endpoint
     * @param {Object} data - Request body data
     * @param {Object} options - Additional options
     * @returns {Promise} API response
     */
    patch: async (endpoint, data = {}, options = {}) => {
      return apiRequest(serviceBaseUrl, endpoint, {
        method: 'PATCH',
        body: JSON.stringify(data),
        ...options,
      });
    },

    /**
     * Make a DELETE request
     * @param {string} endpoint - Endpoint
     * @param {Object} options - Additional options
     * @returns {Promise} API response
     */
    delete: async (endpoint, options = {}) => {
      return apiRequest(serviceBaseUrl, endpoint, {
        method: 'DELETE',
        ...options,
      });
    },

    /**
     * Upload file with FormData
     * @param {string} endpoint - Endpoint
     * @param {FormData} formData - Form data with file
     * @param {Object} options - Additional options
     * @returns {Promise} API response
     */
    upload: async (endpoint, formData, options = {}) => {
      const token = getAuthToken();
      
      return apiRequest(serviceBaseUrl, endpoint, {
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': `Token ${token}`,
          // Don't set Content-Type for FormData - let browser handle it
          ...options.headers,
        },
        ...options,
      });
    },

    /**
     * Get the base URL for this service
     * @returns {string} Service base URL
     */
    getBaseUrl: () => serviceBaseUrl,

    /**
     * Get service name
     * @returns {string} Service name
     */
    getServiceName: () => serviceName,
  };
};

/**
 * Common response transformers
 */
export const responseTransformers = {
  /**
   * Transform paginated response
   * @param {Object} response - Backend response
   * @param {Function} itemTransformer - Function to transform individual items
   * @returns {Object} Transformed response
   */
  paginated: (response, itemTransformer = (item) => item) => {
    const items = response.results || response.data || [];
    return {
      success: true,
      data: {
        results: Array.isArray(items) ? items.map(itemTransformer) : [],
        count: response.count || 0,
        next: response.next || null,
        previous: response.previous || null,
        total: response.total || response.count || 0,
      }
    };
  },

  /**
   * Transform single item response
   * @param {Object} response - Backend response
   * @param {Function} itemTransformer - Function to transform the item
   * @returns {Object} Transformed response
   */
  single: (response, itemTransformer = (item) => item) => {
    return {
      success: true,
      data: itemTransformer(response)
    };
  },

  /**
   * Transform list response
   * @param {Object} response - Backend response
   * @param {Function} itemTransformer - Function to transform individual items
   * @returns {Object} Transformed response
   */
  list: (response, itemTransformer = (item) => item) => {
    const items = Array.isArray(response) ? response : (response.data || response.results || []);
    return {
      success: true,
      data: items.map(itemTransformer)
    };
  },

  /**
   * Transform snake_case keys to camelCase
   * @param {Object} obj - Object to transform
   * @returns {Object} Transformed object
   */
  toCamelCase: (obj) => {
    if (obj === null || obj === undefined || typeof obj !== 'object') {
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map(responseTransformers.toCamelCase);
    }

    const transformed = {};
    Object.keys(obj).forEach(key => {
      const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
      transformed[camelKey] = responseTransformers.toCamelCase(obj[key]);
    });

    return transformed;
  },

  /**
   * Transform camelCase keys to snake_case
   * @param {Object} obj - Object to transform
   * @returns {Object} Transformed object
   */
  toSnakeCase: (obj) => {
    if (obj === null || obj === undefined || typeof obj !== 'object') {
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map(responseTransformers.toSnakeCase);
    }

    const transformed = {};
    Object.keys(obj).forEach(key => {
      const snakeKey = key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
      transformed[snakeKey] = responseTransformers.toSnakeCase(obj[key]);
    });

    return transformed;
  },
};

/**
 * Common error handling utilities
 */
export const errorHandlers = {
  /**
   * Handle API errors with user-friendly messages
   * @param {Error} error - Error object
   * @param {string} context - Context for logging
   * @returns {Object} Formatted error response
   */
  handleApiError: (error, context = '') => {
    console.error(`API Error${context ? ` in ${context}` : ''}:`, error);
    
    return {
      success: false,
      error: {
        message: error.message || 'An unexpected error occurred',
        status: error.status || 500,
        context,
        timestamp: new Date().toISOString(),
      }
    };
  },

  /**
   * Check if error is due to authentication
   * @param {Error} error - Error object
   * @returns {boolean} True if auth error
   */
  isAuthError: (error) => {
    return error.message?.toLowerCase().includes('authentication') ||
           error.message?.toLowerCase().includes('unauthorized') ||
           error.status === 401;
  },

  /**
   * Check if error is due to permissions
   * @param {Error} error - Error object
   * @returns {boolean} True if permission error
   */
  isPermissionError: (error) => {
    return error.message?.toLowerCase().includes('permission') ||
           error.message?.toLowerCase().includes('forbidden') ||
           error.status === 403;
  },

  /**
   * Check if error is due to rate limiting
   * @param {Error} error - Error object
   * @returns {boolean} True if rate limit error
   */
  isRateLimitError: (error) => {
    return error.message?.toLowerCase().includes('rate limit') ||
           error.message?.toLowerCase().includes('too many requests') ||
           error.status === 429;
  },
};

/**
 * Request queue for auto-save functionality
 */
class RequestQueue {
  constructor() {
    this.queue = [];
    this.isProcessing = false;
  }

  /**
   * Add request to queue
   * @param {Function} requestFn - Function that returns a promise
   * @param {number} priority - Request priority (lower = higher priority)
   */
  add(requestFn, priority = 5) {
    this.queue.push({ requestFn, priority, timestamp: Date.now() });
    this.queue.sort((a, b) => a.priority - b.priority);
    
    if (!this.isProcessing) {
      this.process();
    }
  }

  /**
   * Process queued requests
   */
  async process() {
    if (this.isProcessing || this.queue.length === 0) {
      return;
    }
    
    this.isProcessing = true;
    
    while (this.queue.length > 0) {
      const { requestFn } = this.queue.shift();
      
      try {
        await requestFn();
      } catch (error) {
        console.error('Queued request failed:', error);
      }
      
      // Small delay to avoid overwhelming the server
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    this.isProcessing = false;
  }

  /**
   * Clear the queue
   */
  clear() {
    this.queue = [];
  }

  /**
   * Get queue status
   */
  getStatus() {
    return {
      length: this.queue.length,
      isProcessing: this.isProcessing,
    };
  }
}

// Export singleton request queue instance
export const requestQueue = new RequestQueue();

/**
 * Logging utilities for API services
 */
export const apiLogger = {
  /**
   * Log API request start
   * @param {string} service - Service name
   * @param {string} method - HTTP method
   * @param {string} endpoint - Endpoint
   * @param {Object} data - Request data
   */
  logRequest: (service, method, endpoint, data = null) => {
    if (process.env.NODE_ENV === 'development') {
      console.log(`[${service} API] ${method} ${endpoint}`, data ? data : '');
    }
  },

  /**
   * Log API response
   * @param {string} service - Service name
   * @param {string} method - HTTP method
   * @param {string} endpoint - Endpoint
   * @param {boolean} success - Whether request was successful
   * @param {Object} response - Response data
   */
  logResponse: (service, method, endpoint, success, response = null) => {
    if (process.env.NODE_ENV === 'development') {
      const status = success ? 'SUCCESS' : 'ERROR';
      console.log(`[${service} API] ${method} ${endpoint} - ${status}`, response ? response : '');
    }
  },

  /**
   * Log API error
   * @param {string} service - Service name
   * @param {string} method - HTTP method
   * @param {string} endpoint - Endpoint
   * @param {Error} error - Error object
   */
  logError: (service, method, endpoint, error) => {
    console.error(`[${service} API] ${method} ${endpoint} - ERROR:`, error);
  },
};

export default {
  getBaseUrl,
  getAuthToken,
  buildQueryParams,
  apiRequest,
  createApiService,
  responseTransformers,
  errorHandlers,
  requestQueue,
  apiLogger,
}; 